import React from 'react';
import { Pin, Archive, Trash2, Paperclip, CheckSquare, ArchiveRestore } from 'lucide-react';
import { API } from '../lib/api';

export const NOTE_COLORS = {
  default: { bar: 'hsl(var(--border))', tint: 'transparent' },
  amber: { bar: '#f59e0b', tint: 'rgba(245,158,11,0.07)' },
  emerald: { bar: '#10b981', tint: 'rgba(16,185,129,0.07)' },
  sky: { bar: '#38bdf8', tint: 'rgba(56,189,248,0.07)' },
  violet: { bar: '#a78bfa', tint: 'rgba(167,139,250,0.07)' },
  rose: { bar: '#fb7185', tint: 'rgba(251,113,133,0.07)' },
  slate: { bar: '#94a3b8', tint: 'rgba(148,163,184,0.07)' },
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }) : '');

export const NoteCard = ({ note, onOpen, onPin, onArchive, onDelete }) => {
  const c = NOTE_COLORS[note.color] || NOTE_COLORS.default;
  const items = note.items || [];
  const done = items.filter((i) => i.done).length;
  const imgs = (note.attachments || []).filter((a) => a.is_image);

  return (
    <div data-testid={`note-card-${note.id}`} onClick={() => onOpen(note)}
      className="note-card break-inside-avoid mb-3 rounded-[3px] border border-border cursor-pointer transition-all hover:border-primary/50 hover:-translate-y-[1px]"
      style={{ background: c.tint, borderTopWidth: 2, borderTopColor: c.bar }}>
      <div className="p-3 space-y-2">
        <div className="flex items-start gap-2">
          <p className="flex-1 text-[13.5px] font-bold leading-snug break-words" data-testid={`note-title-${note.id}`}>
            {note.title || <span className="t-label font-medium">Sem título</span>}
          </p>
          <button data-testid={`note-pin-${note.id}`} title={note.pinned ? 'Desafixar' : 'Fixar no topo'}
            onClick={(e) => { e.stopPropagation(); onPin(note); }}
            className={`shrink-0 p-1 rounded transition-colors ${note.pinned ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
            <Pin className="w-3.5 h-3.5" style={note.pinned ? { fill: 'currentColor' } : undefined} />
          </button>
        </div>

        {note.body && <p className="text-[12.5px] leading-relaxed text-foreground/75 whitespace-pre-wrap line-clamp-6 break-words">{note.body}</p>}

        {items.length > 0 && (
          <div className="space-y-1" data-testid={`note-items-${note.id}`}>
            {items.slice(0, 4).map((it) => (
              <p key={it.id} className={`text-[12px] flex items-start gap-1.5 ${it.done ? 'line-through text-muted-foreground' : 'text-foreground/80'}`}>
                <CheckSquare className={`w-3 h-3 mt-[2px] shrink-0 ${it.done ? 'text-[hsl(var(--ok))]' : 'text-muted-foreground'}`} />
                <span className="break-words">{it.text}</span>
              </p>
            ))}
            {items.length > 4 && <p className="text-[11px] t-label">+ {items.length - 4} tarefa(s)</p>}
          </div>
        )}

        {imgs.length > 0 && (
          <div className="grid grid-cols-3 gap-1">
            {imgs.slice(0, 3).map((a) => (
              <img key={a.id} src={`${API}/notes/${note.id}/attachments/${a.id}`} alt=""
                className="w-full aspect-square object-cover rounded-[2px] border border-border/70" />
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 pt-1 border-t border-border/60">
          <span className="text-[10.5px] t-label">{fmt(note.updated_at)}</span>
          {items.length > 0 && <span className="ent-pill">{done}/{items.length}</span>}
          {(note.attachments || []).length > 0 && (
            <span className="text-[10.5px] t-label flex items-center gap-1"><Paperclip className="w-3 h-3" />{note.attachments.length}</span>
          )}
          <div className="ml-auto flex items-center gap-0.5">
            <button data-testid={`note-archive-${note.id}`} title={note.archived ? 'Restaurar' : 'Arquivar'}
              onClick={(e) => { e.stopPropagation(); onArchive(note); }}
              className="p-1 rounded text-muted-foreground hover:text-foreground transition-colors">
              {note.archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
            </button>
            <button data-testid={`note-delete-${note.id}`} title="Excluir nota"
              onClick={(e) => { e.stopPropagation(); onDelete(note); }}
              className="p-1 rounded text-muted-foreground hover:text-[hsl(var(--bad))] transition-colors">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
